import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";

const NoteCtx = createContext(null);
export const useNotifications = () => useContext(NoteCtx);

export function NotificationsProvider({ children }) {
  const nav = useNavigate();
  const [state, setState] = useState({ notifications: [], unread: 0 });
  const known = useRef(null);

  const open = useCallback(async (n) => {
    setState((s) => ({ notifications: s.notifications.map((x) => (x.id === n.id ? { ...x, read: true } : x)), unread: Math.max(0, s.unread - (n.read ? 0 : 1)) }));
    api.post(`/notifications/${n.id}/read`).catch(() => {});
    if (n.link) nav(n.link);
  }, [nav]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      const first = known.current === null;
      const seen = known.current || new Set();
      if (!first) {
        data.notifications.filter((n) => !seen.has(n.id) && !n.read).reverse().forEach((n) => {
          toast(<span data-testid="notification-toast">{n.title}</span>, { id: `note-${n.id}`, duration: 6000, action: { label: "Open", onClick: () => open(n) } });
        });
      }
      known.current = new Set(data.notifications.map((n) => n.id));
      setState(data);
    } catch (_) { /* offline: keep what we have */ }
  }, [open]);

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.type === "notification.created");

  const markAll = async () => {
    setState((s) => ({ notifications: s.notifications.map((n) => ({ ...n, read: true })), unread: 0 }));
    await api.post("/notifications/read-all").catch(() => {});
  };

  return <NoteCtx.Provider value={{ ...state, open, markAll, reload: load }}>{children}</NoteCtx.Provider>;
}
